import * as Sentry from '@sentry/react';
import { deleteCookie, isAMD, setCookieValue, useSift } from '@do/seashell';
import { useQuery } from '@do/graphql-client';
import { useLocation } from '@do/seashell/navigation';
import { Box, style } from '@do/walrus';
import React, { useEffect } from 'react';
import {
  APP_PLATFORM_TEMPLATE_COOKIE,
  CHURN_SURVEY_URL,
  ONBOARDING_ORIGIN_PARAM,
  PUBLIC_ENDPOINT,
  PREFERRED_SIGN_IN_COOKIE,
} from '../../constants';
import copy from '../../copy';
import { GetLoginInfo } from '../../generated-types/GetLoginInfo';
import { registerReferralCode, registerReferralCodeVariables } from '../../generated-types/registerReferralCode';
import useActivationRedirectCookie from '../../hooks/use-activation-redirect-cookie';
import useDocumentTitle from '../../hooks/use-document-title';
import useLoginVariants from '../../hooks/use-login-variants';
import useOneTimeCookie from '../../hooks/use-one-time-cookie';
import { LoginStep } from '../../interfaces/login';
import { GET_LOGIN_FORM_DATA, REGISTER_REFERRAL_CODE } from '../../queries/queries';
import { getRefcode } from '../../utils/get-refcode';
import { getSessionData } from '../../utils/gql-utils';
import { getQueryParam } from '../../utils/query-utils';
import { BannerType, HeaderBanner, Loading, Wrap, WrapSplit } from '../Common';
import { Variant } from '../login/loginVariants/Variant';
import ErrorBanner from '../ErrorBanner';
import FormWrap from '../FormWrap';
import Header from '../Header';
import LoginForm from '../login/LoginForm';
import { StepTransitionManager } from '../StepTransitionManager';
import TwoFactorAuthForm from '../TwoFactorAuthForm';
import VerificationForm from '../VerificationForm';

import PoweredByDigitalOcean from './PoweredByDigitalOcean';
import { RegistrationTemplate, useRegistrationTemplate } from '../RegistrationTemplateProvider';
import LegalLoginInfo from './LegalLoginInfo';
import { validateUrl } from './validateUrl/validateUrl';
import TermsAndPolicy from './TermsAndPolicy';

const STEPS = {
  login: 0,
  two_factor_auth: 1,
  two_factor_auth_secondary: 2,
  device_verification: 3,
};

const WrapComponentContent: React.FC<{
  children: React.ReactNode;
  step: number;
  wrap?: boolean;
  template?: RegistrationTemplate;
}> = ({ children, wrap, step, template }) => {
  const WrapComponent = wrap ? WrapSplit : Wrap;

  return (
    <WrapComponent>
      <Box display="block" height="fit-content">
        <FormWrap style={{ paddingBottom: `${style.vars.space['3']}` }}>
          <StepTransitionManager currentStepNum={step}>
            <Box px={style.vars.whitespace.medium} display="block">
              {children}
            </Box>
          </StepTransitionManager>
        </FormWrap>
        {!template?.login.legalInfo && <TermsAndPolicy />}
      </Box>
    </WrapComponent>
  );
};

interface LocationState {
  preferredLoginState?: string;
  success?: 'forgot' | 'reset';
}

const Login: React.FC = () => {
  const location = useLocation();
  const { template } = useRegistrationTemplate();
  const locationState: LocationState = location.state || {};
  const { search } = location;
  const refcode = getRefcode(search);

  const onboardingOrigin = getQueryParam(ONBOARDING_ORIGIN_PARAM, search) || '';

  let { variant } = useLoginVariants();
  if (template.login.variant) {
    variant = template.login.variant;
  }

  useQuery<registerReferralCode, registerReferralCodeVariables>(REGISTER_REFERRAL_CODE, {
    context: {
      url: PUBLIC_ENDPOINT,
    },
    variables: { refcode },
    skip: !refcode,
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Error in register referral code:', error);
    },
  });

  useActivationRedirectCookie();

  const {
    loading: isLoading,
    data,
    refetch: refetchLoginFormData,
  } = useQuery<GetLoginInfo>(GET_LOGIN_FORM_DATA, {
    context: {
      url: PUBLIC_ENDPOINT,
    },
    variables: {
      onboarding_origin: onboardingOrigin,
      redirect_url: getQueryParam('redirect_url', search),
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Error in getting login form data:', error);
    },
  });

  useSift({
    sessionId: data?.loginFormData?.session_id ?? undefined,
    path: location.pathname,
  });

  const isUserAccountDeactivated = useOneTimeCookie('_digitalocean_flash_code', 'user_account_deactivated');

  const [step, setStep] = React.useState(STEPS.login);
  const [config2fa, setConfig2fa] = React.useState<LoginStep>();

  useDocumentTitle(copy.form.login_title);

  const preferredLoginState = locationState.preferredLoginState;
  const user = data?.loginFormData?.user;

  const hasPreferredLogin =
    preferredLoginState !== 'none' && !isLoading && !!user?.email && !!user?.sign_in_method && !!user?.avatar_url;

  /**
   * This handles the case of a new-to-DO user clicking on a "Deploy to DO"
   * button elsewhere on the internet.
   *
   * - The user clicks on the button and is directed to a URL like this:
   *     https://cloud.digitalocean.com/apps/new?template=https://github.com/RocketChat/Rocket.Chat/tree/master
   * - The Edge gateway detects that the user is not logged in and redirects
   *   them to the login form with a `redirect_url` param like this:
   *     https://cloud.digitalocean.com/login?redirect_url=https%3A%2F%2Fcloud.digitalocean.com%2Fapps%2Fnew%3Ftemplate%3Dhttps%3A%2F%2Fgithub.com%2FRocketChat%2FRocket.Chat%2Ftree%2Fmaster
   * - We detect that the `redirect_url` param is a "Deploy to DO" URL.
   * - We set a cookie with the complete redirect URL.
   *
   * Later on in the signup flow, once the user's account is successfully
   * activated, the onboarding app will use this cookie to redirect the user
   * back to the "Deploy to DO" create flow in the App Platform UI.
   */
  useEffect(() => {
    const redirectUrl = getQueryParam('redirect_url', search);
    const { url: parsedRedirectUrl, isValid } = validateUrl(redirectUrl);

    if (!isValid) {
      return;
    }

    setCookieValue(APP_PLATFORM_TEMPLATE_COOKIE, parsedRedirectUrl);
  }, [search]);

  /* AMD users can use Google to sign in when selecting to 'sign in with DigitalOcean', which sets the preferred sign-in method to google.
   However AMD users do not have the option to directly sign in with Google.
   We need to clear the preferred sign-in cookie and update the form data to display the correct options to these AMD users when they're signed out.
  */
  const [clearedPreferredCookie, setClearedPreferredCookie] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);

  const hasPreferredGoogle = user?.sign_in_method === 'google';

  const isAMDAndLoggedInViaDOGoogle = isAMD() && hasPreferredGoogle && !clearedPreferredCookie;

  React.useEffect(() => {
    if (isAMDAndLoggedInViaDOGoogle) {
      deleteCookie(PREFERRED_SIGN_IN_COOKIE);
      // Set refetching state to true before refetching to prevent form from flashing with incorrect options
      setIsRefetching(true);
      // refetch data to update the form options without a page reload
      refetchLoginFormData().finally(() => {
        setIsRefetching(false);
        setClearedPreferredCookie(true);
      });
    }
  }, [isAMDAndLoggedInViaDOGoogle, refetchLoginFormData]);

  if (isLoading || !variant || isRefetching) {
    return <Loading isLogin={true} />;
  }

  const googleOauthUrl = `/sessions/google${search}`;
  const githubOauthUrl = `/sessions/github${search}`;
  const doOauthurl = `/sessions/digitalocean`;

  const oauthError = data?.loginFormData?.oauth_error;
  const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
  const churnSurvey = userUUID ? `${CHURN_SURVEY_URL}?uuid=${userUUID}` : CHURN_SURVEY_URL;

  let form;

  switch (step) {
    case STEPS.login:
      form = (
        <>
          {oauthError && (
            <ErrorBanner error={copy.errors[oauthError as keyof typeof copy.errors] || copy.errors.unknown} />
          )}
          <LoginForm
            googleOauthUrl={googleOauthUrl}
            githubOauthUrl={githubOauthUrl}
            doOauthurl={doOauthurl}
            hasPreferredLogin={hasPreferredLogin}
            user={user ?? undefined}
            team={data?.loginFormData?.team}
            passwordSuccessBanner={locationState.success}
            showNext={(nextStep: LoginStep) => {
              if (STEPS[nextStep.type] === STEPS.two_factor_auth) {
                setConfig2fa(nextStep);
              }
              setStep(STEPS[nextStep.type]);
            }}
            variant={variant}
            allowedAuthMethods={template.allowedAuthMethods}
            buttonStyles={template.buttons}
            inputLabelColor={template.inputLabelColor}
          />
        </>
      );
      break;
    case STEPS.two_factor_auth:
      form = config2fa && (
        <TwoFactorAuthForm
          key={step}
          changeStep={() => setStep(STEPS.two_factor_auth_secondary)}
          type={config2fa.primary}
          nextStepType={config2fa.secondary}
        />
      );

      break;
    case STEPS.two_factor_auth_secondary:
      form = config2fa?.secondary && (
        <TwoFactorAuthForm
          key={step}
          changeStep={() => setStep(STEPS.two_factor_auth)}
          type={config2fa.secondary}
          nextStepType={config2fa.primary}
          isSecondary
        />
      );
      break;
    case STEPS.device_verification:
      form = <VerificationForm goBack={() => setStep(STEPS.login)} />;
      break;
  }

  return (
    <>
      <Header isLogin={true} queryString={search} />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}

      <Variant variant={variant} buttonStyles={template.buttons}>
        <WrapComponentContent wrap={!!variant.splash} step={step} template={template}>
          {form}
        </WrapComponentContent>
        {template.login.poweredByDigitalOcean && <PoweredByDigitalOcean />}
        {template.login.legalInfo && <LegalLoginInfo />}
      </Variant>
    </>
  );
};

export default Login;
